import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { broadcasts, billing } from '@repletoryreach/sdk-api';
import { useMediaQuery } from '@repletoryreach/sdk-utils/hooks';
import { Anchor, Badge, Button, Card, Group, Progress, Stack, Text } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, StatusBadge, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Menu } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useCredits } from '../../lib/credits';
import { HelpButton } from '../../app/guides/HelpButton';
import { useT } from '../../i18n/I18nProvider';
import { formatDateTime, formatCount, type Indexed } from '../../lib/format';
import { usePersistedState } from '../../lib/usePersistedState';
import { startErrorMessage, cancelErrorMessage } from './components/startError';
import { BroadcastCards } from './components/BroadcastCards';
import { ChannelBadge, broadcastName, hasTemplate } from './components/channel';
import {
  attemptedOf, broadcastFailureText, broadcastStatusLabel, formatRate, pollInterval, progressOf, rate, spendLines,
  RUNNING_POLL_MS, REPORT_POLL_MS
} from './components/delivery';
import './broadcasts.css';

const PAGE_SIZE = 25;

/** Status codes the list can filter by, in workflow order. */
const STATUSES = ['scheduled', 'running', 'completed', 'failed', 'cancelled'] as const;

export default function BroadcastsPage() {
  const t = useT();
  const credits = useCredits();
  const qc = useQueryClient();
  const nav = useNavigate();
  const narrow = useMediaQuery('(max-width: 820px)'); // i18n-ignore: CSS media query
  const [status, setStatus] = usePersistedState('tenant.broadcasts.status', '');
  const [page, setPage] = useState(1);

  const { data, isPending, error, isFetching } = useQuery({
    queryKey: ['broadcasts', 'list', status, page],
    queryFn: () => broadcasts.listBroadcasts({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      ...(status ? { status } : {})
    }),
    placeholderData: (prev) => prev,
    /*
     * §4 item 8: the list neither polled nor subscribed, so there was nothing
     * between "send" and "delivered". `BroadcastEvents` is declared in
     * sdk-realtime but there is no broadcasts hub, nothing publishes
     * `broadcast.progress`, and `registerHub` is not exported: a type with no
     * producer and no transport. Polling only while something is actually
     * running is the honest option, and it costs nothing when nothing is.
     *
     * 4 October 2026: delivery and read reports keep arriving after a broadcast finishes, so a page with one that
     * finished in the last 48 hours and still has people with no report keeps asking, every thirty seconds.
     */
    refetchInterval: (q) => listPoll(q.state.data?.data ?? [])
  });

  useEffect(() => { setPage(1); }, [status]);

  const rows = (data?.data ?? []) as Indexed<broadcasts.Broadcast>[];
  // What each campaign on this page cost, in one lookup against the ledger.
  const spendQ = useQuery({
    queryKey: ['usage', 'sources', 'broadcast', rows.map((b) => b.id).join(',')],
    enabled: rows.length > 0,
    queryFn: () => billing.getUsageSources('broadcast', rows.map((b) => b.id)),
    retry: false,
    // A late failure is refunded after the broadcast finished, so spend is asked for as long as the rows are.
    refetchInterval: () => { const p = listPoll(rows); return p === false ? false : Math.max(p, REPORT_POLL_MS); }
  });
  const spendById = useMemo(() => new Map((spendQ.data?.data ?? []).map((r) => [r.id, r])), [spendQ.data]);
  const total = data?.meta?.total ?? 0;

  const start = useMutation({
    mutationFn: (b: broadcasts.Broadcast) => broadcasts.startBroadcast(b.id),
    // §4 item 4: both were silent on success AND failure, so a failed cancel
    // looked exactly like one that worked.
    onSuccess: (_r, b) => {
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      notifySuccess(t('broadcasts.view.lifecycle.toast.started', { template: broadcastName(t, b) }), t('broadcasts.title'));
    },
    onError: (e) => notifyError(startErrorMessage(t, e), t('broadcasts.view.lifecycle.toast.startFailed'))
  });

  const cancel = useMutation({
    mutationFn: (b: broadcasts.Broadcast) => broadcasts.cancelBroadcast(b.id),
    onSuccess: (_r, b) => {
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      notifySuccess(t('broadcasts.view.lifecycle.toast.cancelled', { template: broadcastName(t, b) }), t('broadcasts.title'));
    },
    onError: (e) => notifyError(cancelErrorMessage(t, e), t('broadcasts.view.lifecycle.toast.cancelFailed'))
  });

  /** §4 item 5: cancelling is irreversible and had no confirmation here. */
  function confirmCancel(b: broadcasts.Broadcast) {
    // Still queued is what a cancel stops: everyone already sent, failed or left out is done either way.
    const remaining = progressOf(b).queued;
    openConfirmModal({
      title: t('broadcasts.modal.cancel.title'),
      children: (
        <Stack gap={8}>
          <Text role="bodySmall">{t('broadcasts.modal.cancel.body', { template: broadcastName(t, b) })}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('broadcasts.modal.cancel.remaining', { remaining, sent: b.recipients_sent })}
          </Text>
        </Stack>
      ),
      labels: { confirm: t('broadcasts.modal.cancel.confirm'), cancel: t('action.back') },
      confirmProps: { color: 'red' },
      onConfirm: () => cancel.mutate(b)
    });
  }

  const actionsFor = (b: broadcasts.Broadcast) => (
    <Menu>
      <Menu.Target>
        {/* §4 item 17: the row buttons were bare "Start"/"Cancel" with no row
            identity, so a screen reader heard the same two words per row. */}
        <Button size="xs" variant="subtle" aria-label={t('broadcasts.actions.for', { template: broadcastName(t, b) })}>⋯</Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => nav(`/broadcasts/${b.id}`)}>{t('action.view')}</Menu.Item>
        {(b.status_code === 'scheduled' || b.status_code === 'draft') && (
          <Menu.Item onClick={() => start.mutate(b)}>{t('action.start')}</Menu.Item>
        )}
        {/* §4 item 14: Cancel was offered for `failed` broadcasts, where there
            is nothing left to cancel. */}
        {(b.status_code === 'scheduled' || b.status_code === 'running' || b.status_code === 'draft') && (
          <>
            <Menu.Divider />
            <Menu.Item color="red" onClick={() => confirmCancel(b)}>{t('action.cancel')}</Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('broadcasts.title')}
        description={t('broadcasts.description')}
        withDivider={false}
        /* Creating a broadcast is a page of its own now (`/broadcasts/new`).
           It used to be a drawer over this list: four questions, real money and
           an audience that cannot be recalled, in a 480px column. */
        /* A broadcast goes to a group. Someone with a handful of people wants
           Compose, and this is where they look first, so say so here rather
           than leaving them to discover an empty tag list. */
        actions={(
          <Group gap="sm" wrap="wrap" align="center">
            <HelpButton guide="audience" />
            <Button size="sm" data-guide="broadcasts-new" onClick={() => nav('/broadcasts/new')}>{t('broadcasts.actions.new')}</Button>
            <Button size="sm" variant="default" data-guide="broadcasts-delivery" onClick={() => nav('/broadcasts/delivery')}>{t('broadcasts.delivery.title')}</Button>
            <Anchor href="/inbox/compose">{t('broadcasts.composeInstead')}</Anchor>
          </Group>
        )}
      />

      {error && <Alert color="error" role="alert">{t('broadcasts.loadError')}</Alert>}

      {/* §4 item 7: `?status=` has been server-side ready and unused. */}
      <div className="rr-broadcasts__filters" role="tablist" aria-label={t('common.status')}>
        <button
          type="button"
          role="tab"
          aria-selected={status === ''}
          className={`rr-broadcasts__chip${status === '' ? ' is-active' : ''}`}
          onClick={() => setStatus('')}
        >
          {t('common.all')}
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={status === s}
            className={`rr-broadcasts__chip${status === s ? ' is-active' : ''}`}
            onClick={() => setStatus(s)}
          >
            {t(`broadcasts.status.${s}`, s)}
          </button>
        ))}
        <Text role="caption" c="var(--rr-color-text-muted)" className="rr-broadcasts__count">
          {t('contacts.shownOf', { count: rows.length, total })}
        </Text>
      </div>

      {/* One anchor for the guide, whichever way the list shows: empty, as cards or as a table. */}
      <div data-guide="broadcasts-list">
      {!isPending && rows.length === 0 ? (
        <Card withBorder>
          <EmptyState
            title={status ? t('broadcasts.emptyFiltered') : t('broadcasts.empty')}
            description={status ? t('broadcasts.emptyFiltered.hint') : t('broadcasts.empty.hint')}
            action={
              status
                ? <Button size="xs" variant="default" onClick={() => setStatus('')}>{t('inbox.filters.clear')}</Button>
                : <Button size="xs" onClick={() => nav('/broadcasts/new')}>{t('broadcasts.actions.new')}</Button>
            }
          />
        </Card>
      ) : narrow ? (
        <BroadcastCards
          rows={rows}
          loading={isPending}
          onOpen={(b) => nav(`/broadcasts/${b.id}`)}
          actions={actionsFor}
          spendFor={(b) => spendQ.isPending ? '…' : spendLines(t, credits, spendById.get(b.id), b.estimated_currency).net}
        />
      ) : (
        <Card withBorder noPadding>
          <DataTable<Indexed<broadcasts.Broadcast>>
            records={rows}
            loading={isPending || isFetching}
            idAccessor="id"
            onRowClick={(b) => nav(`/broadcasts/${b.id}`)}
            page={page}
            totalRecords={total}
            recordsPerPage={PAGE_SIZE}
            onPageChange={setPage}
            emptyState={t('broadcasts.empty')}
            columns={[
              {
                accessor: 'template_code',
                title: t('broadcasts.columns.template'),
                // An SMS or email broadcast has no template: its badge and its text or subject instead.
                render: (b) => !hasTemplate(b)
                  ? (
                    <Group gap={6} wrap="nowrap">
                      <ChannelBadge broadcast={b} t={t} />
                      <Text role="bodySmall">{broadcastName(t, b)}</Text>
                    </Group>
                  )
                  : <code>{b.template_code}</code>
              },
              {
                accessor: 'status_code',
                title: t('common.status'),
                render: (b) => {
                  // A failed broadcast says why (4 October 2026): no credit when it was due, or nobody reached.
                  const why = b.status_code === 'failed' ? broadcastFailureText(t, b) : null;
                  return (
                    <Stack gap={2} style={{ maxWidth: 220 }}>
                      <StatusBadge status={statusTone(b.status_code)}>{broadcastStatusLabel(t, b.status_code)}</StatusBadge>
                      {why && <Text role="caption" c="var(--rr-color-error)" lineClamp={2} title={why}>{why}</Text>}
                    </Stack>
                  );
                }
              },
              {
                accessor: 'recipients_total',
                title: t('broadcasts.columns.sentTotal'),
                // A running campaign wants a bar, not two numbers: the useful
                // question is "how far through is it?".
                render: (b) => <SendProgress broadcast={b} />
              },
              {
                accessor: 'recipients_delivered',
                title: t('broadcasts.recipient.delivered'),
                // How far it reached, read included; and on WhatsApp how many read it.
                render: (b) => <DeliveredCell broadcast={b} />
              },
              {
                accessor: 'recipients_failed',
                title: t('status.failed'),
                render: (b) => b.recipients_failed > 0
                  ? <Badge size="xs" color="red" variant="light">{formatCount(b.recipients_failed)}</Badge>
                  : <Text role="caption" c="var(--rr-color-text-muted)">0</Text>
              },
              {
                accessor: 'spend',
                title: t('broadcasts.columns.spend'),
                textAlign: 'right',
                render: (b) => {
                  const r = spendById.get(b.id);
                  if (!r) return <Text role="caption" c="var(--rr-color-text-muted)">{spendQ.isPending ? '…' : spendLines(t, credits, null, b.estimated_currency).net}</Text>;
                  // Net of refunds, with the two halves when anything was given back.
                  const s = spendLines(t, credits, r);
                  return (
                    <Stack gap={0} align="flex-end">
                      <Text role="bodySmall" fw={600}>{s.net}</Text>
                      {s.detail && <Text role="caption" c="var(--rr-color-text-muted)">{s.detail}</Text>}
                    </Stack>
                  );
                }
              },
              { accessor: 'created_at', title: t('common.created'), render: (b) => formatDateTime(b.created_at) },
              {
                accessor: 'actions',
                title: t('common.actions'),
                render: (b) => <div onClick={(e) => e.stopPropagation()}>{actionsFor(b)}</div>
              }
            ]}
          />
        </Card>
      )}
      </div>
    </Stack>
  );
}

/**
 * How far through its recipients a broadcast is. Done is everything no longer queued, so a broadcast whose last
 * people failed or were left out reaches the end of the bar (4 October 2026: it used to stop at sent / total).
 */
export function SendProgress({ broadcast: b }: { broadcast: broadcasts.Broadcast }) {
  const t = useT();
  const { pct } = useMemo(() => progressOf(b), [b]);

  return (
    <Stack gap={2} style={{ minWidth: 120 }}>
      <Text role="caption">
        {t('broadcasts.progress', {
          sent: formatCount(b.recipients_sent),
          total: formatCount(b.recipients_total)
        })}
      </Text>
      {b.status_code === 'running' && (
        <Progress value={pct} size="xs" radius="xl" aria-label={t('broadcasts.progress.done', { percent: pct })} />
      )}
    </Stack>
  );
}

/** Delivered (read included) and its rate; on WhatsApp, read and its rate under it. */
export function DeliveredCell({ broadcast: b }: { broadcast: broadcasts.Broadcast }) {
  const t = useT();
  if (b.recipients_delivered === undefined || !b.started_at) {
    return <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>;
  }
  const attempted = attemptedOf(b);
  const deliveredRate = rate(b.recipients_delivered, attempted);
  const whatsapp = (b.channel ?? 'whatsapp') === 'whatsapp';
  return (
    <Stack gap={0} data-testid="broadcast-delivered">
      <Text role="bodySmall">
        {deliveredRate == null
          ? formatCount(b.recipients_delivered)
          : t('broadcasts.list.delivered', { count: formatCount(b.recipients_delivered), rate: formatRate(deliveredRate) })}
      </Text>
      {whatsapp && (b.recipients_read ?? 0) > 0 && (
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('broadcasts.list.read', { count: formatCount(b.recipients_read ?? 0), rate: formatRate(rate(b.recipients_read ?? 0, attempted)) })}
        </Text>
      )}
    </Stack>
  );
}

/** The quickest any row on the page needs asking again, or false when none does. */
function listPoll(rows: broadcasts.Broadcast[]): number | false {
  let best: number | false = false;
  for (const b of rows) {
    const p = pollInterval(b);
    if (p !== false && (best === false || p < best)) best = p;
    if (best === RUNNING_POLL_MS) break;
  }
  return best;
}

export function statusTone(s: string): 'healthy' | 'warning' | 'inactive' | 'pending' | 'down' {
  switch (s) {
    case 'completed': return 'healthy';
    case 'running': return 'pending';
    case 'scheduled': return 'inactive';
    case 'failed': return 'down';
    case 'cancelled': return 'warning';
    default: return 'inactive';
  }
}
